import React, { useState, useEffect, useRef } from "react";
import { db, collection, doc, getDocs, onSnapshot, fmt } from "../config/firebase";
import { useCart } from "../context/CartContext";
import "./ProductGrid.css";

const PAGE_SIZE = 12;
const CATEGORY_COLLAPSED_COUNT = 5;

// Maps a category name to a small inline icon. Falls back to a generic
// tag icon for anything that doesn't match a known keyword, so new
// categories added from the admin panel never render blank.
const CategoryIcon = ({ category }) => {
  const key = (category || "").toLowerCase();

  if (category === "All") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"></rect>
        <rect x="14" y="3" width="7" height="7" rx="1.5"></rect>
        <rect x="3" y="14" width="7" height="7" rx="1.5"></rect>
        <rect x="14" y="14" width="7" height="7" rx="1.5"></rect>
      </svg>
    );
  }
  if (key.includes("pen") || key.includes("pencil")) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
        <path d="M2 2l7.586 7.586"></path>
        <circle cx="11" cy="11" r="2"></circle>
      </svg>
    );
  }
  if (key.includes("note") || key.includes("book") || key.includes("diary")) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
      </svg>
    );
  }
  if (key.includes("art") || key.includes("paint") || key.includes("colou") || key.includes("color")) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a10 10 0 1 0 0 20c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1.1.9-2 2-2h2.4c1.7 0 3.1-1.4 3.1-3.1C20.5 6.6 16.7 2 12 2z"></path>
        <circle cx="6.5" cy="11.5" r="1.5"></circle>
        <circle cx="9.5" cy="7.5" r="1.5"></circle>
        <circle cx="14.5" cy="7.5" r="1.5"></circle>
        <circle cx="17.5" cy="11.5" r="1.5"></circle>
      </svg>
    );
  }
  if (key.includes("office") || key.includes("file") || key.includes("stapl") || key.includes("clip")) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.19 9.19a1.5 1.5 0 0 1-2.12-2.12l8.49-8.49"></path>
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41L11 3.83A2 2 0 0 0 9.59 3.24H4a1 1 0 0 0-1 1v5.59a2 2 0 0 0 .59 1.41l9.58 9.58a2 2 0 0 0 2.82 0l5.59-5.59a2 2 0 0 0 .01-2.82z"></path>
      <circle cx="7.5" cy="7.5" r="1.25" fill="currentColor" stroke="none"></circle>
    </svg>
  );
};

const ProductGrid = ({ jumpTarget }) => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("none");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { cart, addToCart, updateCartQty } = useCart();
  const [selectedVariants, setSelectedVariants] = useState({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [zoomImage, setZoomImage] = useState(null);
  const productRefs = useRef({});

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "hiddenCategories"),
      (snap) => {
        if (snap.exists()) {
          setHiddenCategories(snap.data().categories || []);
        } else {
          setHiddenCategories([]);
        }
      },
      (err) => {
        console.error("Error fetching hidden categories:", err);
      }
    );
    return () => unsubscribe();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "products"));
      const items = [];
      const cats = new Set(["All"]);
      const initialVariants = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        items.push({ id: docSnap.id, ...data });
        if (data.category) cats.add(data.category);
        if (data.variants && data.variants.length > 0) {
          initialVariants[docSnap.id] = data.variants[0].label;
        }
      });
      setProducts(items);
      setFiltered(items);
      setCategories(Array.from(cats));
      setSelectedVariants(initialVariants);
    } catch (err) {
      setError("Failed to load products. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEffectivePrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants[0].price;
    }
    return product.price;
  };

  const buildDiverseOrder = (items) => {
    const byCategory = {};
    items.forEach((p) => {
      const cat = p.category || "Uncategorized";
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(p);
    });
    const catNames = Object.keys(byCategory);
    const result = [];
    let added = true;
    while (added) {
      added = false;
      for (const cat of catNames) {
        if (byCategory[cat].length > 0) {
          result.push(byCategory[cat].shift());
          added = true;
        }
      }
    }
    return result;
  };

  const matchesSearch = (product, q) => {
    if (product.name?.toLowerCase().includes(q)) return true;
    if (product.description?.toLowerCase().includes(q)) return true;
    if (product.category?.toLowerCase().includes(q)) return true;
    if (product.variants && product.variants.length > 0) {
      return product.variants.some((v) => v.label?.toLowerCase().includes(q));
    }
    return false;
  };

  const visibleCategories = categories.filter(
    (cat) => cat === "All" || !hiddenCategories.includes(cat)
  );

  // Only show the first N categories unless "See More" has been clicked
  const categoriesToShow = showAllCategories
    ? visibleCategories
    : visibleCategories.slice(0, CATEGORY_COLLAPSED_COUNT);
  const hiddenCategoryCount = visibleCategories.length - categoriesToShow.length;

  useEffect(() => {
    let result = products.filter((p) => !hiddenCategories.includes(p.category));

    if (activeCategory !== "All") {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) => matchesSearch(p, q));
    }

    if (sortOrder === "lowToHigh") {
      result = [...result].sort((a, b) => getEffectivePrice(a) - getEffectivePrice(b));
    } else if (sortOrder === "highToLow") {
      result = [...result].sort((a, b) => getEffectivePrice(b) - getEffectivePrice(a));
    } else {
      // Featured products (set from the admin panel) always lead the
      // list, in the order the admin arranged them. Everything else
      // keeps its normal ordering behind them, and the total visible
      // count is untouched — this only changes ORDER, not how many
      // products load.
      const featured = result
        .filter((p) => p.featured)
        .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));
      const rest = result.filter((p) => !p.featured);

      const orderedRest =
        activeCategory === "All" && !search.trim() ? buildDiverseOrder(rest) : rest;

      result = [...featured, ...orderedRest];
    }

    setFiltered(result);
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory, search, products, sortOrder, hiddenCategories]);

  useEffect(() => {
    if (activeCategory !== "All" && hiddenCategories.includes(activeCategory)) {
      setActiveCategory("All");
    }
  }, [hiddenCategories, activeCategory]);

  useEffect(() => {
    if (jumpTarget) {
      setSearch("");
      setSortOrder("none");
      setActiveCategory(jumpTarget.category || "All");
      setShowAllCategories(true); // expand categories so the jumped-to one is visible/selected
      setHighlightedId(jumpTarget.productId);
    }
  }, [jumpTarget]);

  useEffect(() => {
    if (highlightedId && productRefs.current[highlightedId]) {
      productRefs.current[highlightedId].scrollIntoView({ behavior: "smooth", block: "center" });
      const timer = setTimeout(() => setHighlightedId(null), 2200);
      return () => clearTimeout(timer);
    }
  }, [highlightedId, filtered]);

  useEffect(() => {
    if (!zoomImage) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setZoomImage(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomImage]);

  const handleVariantChange = (productId, variantLabel) => {
    setSelectedVariants((prev) => ({ ...prev, [productId]: variantLabel }));
  };

  const getDisplayPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const selectedLabel = selectedVariants[product.id];
      const variant = product.variants.find((v) => v.label === selectedLabel);
      return variant ? variant.price : product.variants[0].price;
    }
    return product.price;
  };

  const getOriginalPrice = (product) => {
    const current = getDisplayPrice(product);
    let original = null;

    if (product.variants && product.variants.length > 0) {
      const selectedLabel = selectedVariants[product.id];
      const variant = product.variants.find((v) => v.label === selectedLabel) || product.variants[0];
      if (variant?.originalPrice) original = Number(variant.originalPrice);
    }

    if (!original) {
      original = Number(product.originalPrice);
    }

    if (!original || isNaN(original) || original <= current) return null;
    return original;
  };

  const getDiscountPercent = (original, current) => {
    if (!original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const getCartQuantity = (productId, variantLabel) => {
    const item = cart.find(
      (i) => i.productId === productId && i.variantLabel === (variantLabel || null)
    );
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (product) => {
    const variant = selectedVariants[product.id] || null;
    addToCart(product, variant);
  };

  const handleIncrement = (product) => {
    const variant = selectedVariants[product.id] || null;
    const currentQty = getCartQuantity(product.id, variant);
    if (currentQty === 0) {
      addToCart(product, variant);
    } else {
      updateCartQty(product.id, variant, currentQty + 1);
    }
  };

  const handleDecrement = (product) => {
    const variant = selectedVariants[product.id] || null;
    const currentQty = getCartQuantity(product.id, variant);
    updateCartQty(product.id, variant, currentQty - 1);
  };

  const visibleProducts = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <section id="products" className="product-section">
      <div className="product-container">
        <div className="product-section-header">
          <h2 className="section-title-inline">Shop by Category</h2>
          <button
            className="see-all-link"
            onClick={() => setShowAllCategories((prev) => !prev)}
          >
            {showAllCategories ? "Show Less" : "View All"}
          </button>
        </div>

        {/* Category icon-grid, replaces the old pill buttons */}
        <div className="category-icon-grid">
          {categoriesToShow.map((cat) => (
            <button
              key={cat}
              className={`category-icon-btn ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              <span className="category-icon-box">
                <CategoryIcon category={cat} />
              </span>
              <span className="category-icon-label">{cat}</span>
            </button>
          ))}
          {!showAllCategories && hiddenCategoryCount > 0 && (
            <button
              className="category-icon-btn category-icon-more"
              onClick={() => setShowAllCategories(true)}
            >
              <span className="category-icon-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <circle cx="5" cy="12" r="2"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                  <circle cx="19" cy="12" r="2"></circle>
                </svg>
              </span>
              <span className="category-icon-label">More</span>
            </button>
          )}
        </div>

        <h2 className="section-title">Our Products</h2>

        <div className="product-controls">
          <div className="search-bar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="sort-bar">
            <label htmlFor="price-sort">Sort by:</label>
            <select
              id="price-sort"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="none">Default</option>
              <option value="lowToHigh">Price: Low to High</option>
              <option value="highToLow">Price: High to Low</option>
            </select>
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {!loading && filtered.length === 0 && (
          <div className="no-results">
            <p>No products found matching your criteria.</p>
            <button className="btn-clear" onClick={() => { setSearch(""); setActiveCategory("All"); setSortOrder("none"); }}>
              Clear Filters
            </button>
          </div>
        )}

        <div className="product-grid">
          {visibleProducts.map((product) => {
            const selectedVariant = selectedVariants[product.id] || null;
            const qtyInCart = getCartQuantity(product.id, selectedVariant);
            const isHighlighted = highlightedId === product.id;
            const currentPrice = getDisplayPrice(product);
            const originalPrice = getOriginalPrice(product);
            const discountPercent = originalPrice ? getDiscountPercent(originalPrice, currentPrice) : 0;
            const imageUrl = product.image || "https://picsum.photos/400/300";

            return (
              <div
                key={product.id}
                ref={(el) => (productRefs.current[product.id] = el)}
                className={`product-card ${isHighlighted ? "highlighted" : ""}`}
              >
                <div
                  className="product-image-wrapper product-image-clickable"
                  onClick={() => setZoomImage({ src: imageUrl, alt: product.name })}
                >
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="product-image"
                    loading="lazy"
                    onError={(e) => { e.target.src = "https://picsum.photos/400/300"; }}
                  />
                  {discountPercent > 0 && (
                    <span className="discount-badge">{discountPercent}% OFF</span>
                  )}
                  {product.stock <= 5 && product.stock > 0 && (
                    <span className="stock-badge low">Only {product.stock} left</span>
                  )}
                  {product.stock === 0 && (
                    <span className="stock-badge out">Out of Stock</span>
                  )}
                  <span className="zoom-hint">🔍</span>
                </div>
                <div className="product-info">
                  <span className="product-category">{product.category}</span>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-desc">{product.description}</p>

                  {product.variants && product.variants.length > 0 && (
                    <div className="variant-select">
                      <label htmlFor={`variant-${product.id}`}>Variant:</label>
                      <select
                        id={`variant-${product.id}`}
                        value={selectedVariants[product.id] || product.variants[0].label}
                        onChange={(e) => handleVariantChange(product.id, e.target.value)}
                      >
                        {product.variants.map((v, i) => (
                          <option key={i} value={v.label}>
                            {v.label} - {fmt(v.price)}
                            {v.originalPrice && v.originalPrice > v.price ? ` (MRP ${fmt(v.originalPrice)})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="product-footer">
                    <div className="price-block">
                      {originalPrice && (
                        <span className="product-price-original">{fmt(originalPrice)}</span>
                      )}
                      <span className="product-price">
                        {fmt(currentPrice)}
                      </span>
                    </div>

                    {qtyInCart > 0 ? (
                      <div className="qty-stepper">
                        <button aria-label="Decrease quantity" onClick={() => handleDecrement(product)}>−</button>
                        <span>{qtyInCart}</span>
                        <button
                          aria-label="Increase quantity"
                          onClick={() => handleIncrement(product)}
                          disabled={qtyInCart >= product.stock}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        className="add-cart-btn"
                        onClick={() => handleAddToCart(product)}
                        disabled={product.stock === 0}
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && hasMore && (
          <div className="load-more-wrapper">
            <button
              className="btn-load-more"
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {zoomImage && (
        <div className="image-zoom-overlay" onClick={() => setZoomImage(null)}>
          <button className="image-zoom-close" onClick={() => setZoomImage(null)}>×</button>
          <img
            src={zoomImage.src}
            alt={zoomImage.alt}
            className="image-zoom-content"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default ProductGrid;